// src/components/home/useDemoAnimation.js
// Hooks partagés par les animations de la landing.
// Objectif : n'animer que ce qui est visible, et rien du tout en reduced-motion.
import { useCallback, useEffect, useRef, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** true si l'utilisateur a demandé à limiter les animations. */
export const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(REDUCED_MOTION_QUERY).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event) => setReduced(event.matches);
    // Safari < 14 ne connaît que addListener
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return reduced;
};

/** true tant que l'élément référencé recoupe le viewport. */
export const useInViewport = (ref, rootMargin = '0px') => {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Filet de sécurité : sans IntersectionObserver, on considère l'élément visible
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return inView;
};

/** false quand l'onglet est en arrière-plan. */
export const usePageVisible = () => {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );

  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  return visible;
};

/**
 * Indique si une animation rattachée à `ref` doit tourner :
 * visible à l'écran, onglet actif, et pas de reduced-motion.
 */
export const useAnimationGate = (ref) => {
  const reduced = usePrefersReducedMotion();
  const inView = useInViewport(ref);
  const pageVisible = usePageVisible();
  return { running: inView && pageVisible && !reduced, reduced };
};

/**
 * Pilote une boucle de scènes découpées en étapes.
 * `sceneDurations[s][k]` = durée (ms) de l'étape k de la scène s ; après la
 * dernière étape on passe à la scène suivante (et on reboucle).
 *
 * Un seul setTimeout vit à la fois. Quand `running` repasse à false, le temps
 * restant de l'étape est mémorisé pour reprendre exactement là où on en était
 * (la barre de progression CSS, mise en pause en parallèle, reste synchrone).
 *
 * `sceneDurations` doit être une référence stable (constante de module).
 */
export const useSceneLoop = (sceneDurations, running) => {
  // `run` change à chaque saut manuel pour relancer les animations CSS ;
  // `round` s'incrémente à chaque tour complet de la boucle.
  const [pos, setPos] = useState({ scene: 0, step: 0, run: 0, round: 0 });
  const pausedRef = useRef(null); // { key, remaining }

  const key = `${pos.run}:${pos.round}:${pos.scene}:${pos.step}`;

  useEffect(() => {
    if (!running) return undefined;

    const paused = pausedRef.current;
    pausedRef.current = null;
    const delay = paused && paused.key === key
      ? paused.remaining
      : sceneDurations[pos.scene][pos.step];

    const startedAt = Date.now();
    let fired = false;

    const id = setTimeout(() => {
      fired = true;
      setPos((p) => {
        if (p.step + 1 < sceneDurations[p.scene].length) return { ...p, step: p.step + 1 };
        const scene = (p.scene + 1) % sceneDurations.length;
        return { ...p, scene, step: 0, round: scene === 0 ? p.round + 1 : p.round };
      });
    }, delay);

    return () => {
      clearTimeout(id);
      if (!fired) pausedRef.current = { key, remaining: Math.max(0, delay - (Date.now() - startedAt)) };
    };
  }, [running, key, pos.scene, pos.step, sceneDurations]);

  const goTo = useCallback((scene) => {
    pausedRef.current = null;
    setPos((p) => ({ scene, step: 0, run: p.run + 1, round: p.round }));
  }, []);

  return { ...pos, goTo };
};

/**
 * Rang d'affichage de chaque ligne (index = ordre de dépôt).
 * Les `revealed` premières lignes ont un score : elles sont triées par score
 * décroissant ; les suivantes restent en bas, dans l'ordre de dépôt.
 */
export const rankRows = (rows, revealed) => {
  const scoreOf = (i) => (i < revealed ? rows[i].score : -1);
  const order = rows.map((_, i) => i).sort((a, b) => scoreOf(b) - scoreOf(a) || a - b);
  const ranks = [];
  order.forEach((rowIndex, rank) => { ranks[rowIndex] = rank; });
  return ranks;
};

export const scoreTone = (score) => {
  if (score >= 75) return 'high';
  if (score >= 50) return 'mid';
  return 'low';
};
